import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faHistory, faPencil, faRemove, faTruckMoving, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import moment from 'moment';
import { find } from 'lodash';

import { TooltipPlacement } from '@shared/components/bootstrap/tooltip';

import { DispatcherAssignmentsManager } from '@admin/components/dispatcher/dispatcher_assignments_manager';
import { MoverErrorIcon } from '@admin/components/dispatcher/mover_error_icon';
import { SimulationErrorIcon } from '@admin/components/dispatcher/simulation_error_icon';
import { ErrorIcon, Status } from '@admin/components/helpers/error_icon';
import { dispatchURL } from '@admin/config/routes';

import { AutoDispatchEnabledCheckbox } from './auto_dispatch_enabled_checkbox';
import { DISPATCHER_CONSTANTS, DISPATCHER_LAYOUT_REFRESH_EVENT } from './dispatcher_constants';
import { isRouteEditable } from './dispatcher_editor_utils';
import { DispatcherInstructionsButton } from './dispatcher_instructions_button';
import { DispatcherOrderPreview } from './dispatcher_order_preview';
import { positionForOrder, positionForTime, toPercentageStyle } from './dispatcher_position';
import type { DispatcherDispatchRecord, DispatcherOrderGrouping, DispatcherOrderRecord } from './dispatcher_types';
import { DispatcherVehicleRequestButton } from './dispatcher_vehicle_request_button';
import { localForDateTimeWithTZ, utcForValueWithTZ } from './tz_date_time';

type NamedResource = {
  id: number | string;
  name: string;
  region_ids?: number[];
  active?: boolean;
};

type DispatchOrder = DispatcherOrderRecord;

type DispatchGrouping = DispatcherOrderGrouping;

type DispatchData = DispatcherDispatchRecord;

type MoverCountError = {
  message: string;
  status: Status;
};

export type DispatcherDispatchProps = {
  dispatch: DispatchData;
  region: { id: number; tz: string };
  date: Date | string;
  depots: NamedResource[];
  warehouses: NamedResource[];
  fieldShiftAssignments: object[];
  vehicleTypes: { id: number | string; name: string }[];
  users: object[];
  filters: { region_id: number; date: Date | string };
  mode: 'viewing' | 'modifying';
  routeIndex: number;
  color: string;
  dispatchHasWarehouseOrder: boolean;
  moverCountErrors: MoverCountError[];
  vehicleRequestIconStatus: Status;
  vehicleRequestErrors: string[];
  onRemove: (dispatch: DispatchData) => void;
  onDragstart: (order: DispatchOrder) => void;
  onDragmove: (order: DispatchOrder, event: DragEvent) => void;
  onDragend: (order: DispatchOrder) => void;
  routeLockActive?: boolean;
};

const isWarehouseOrder = (order: DispatchOrder) =>
  order.address?.addressable_type === 'Warehouse' && order.type !== 'restock' && order.service_type !== 'drop_off';

const dispatchHasWarehouseOrder = (dispatch: DispatchData) => dispatch.orders.some(isWarehouseOrder);

const addonsFormatted = (addons: string[]) => addons.join(', ');

const vehicleRequestFormatted = (
  dispatch: DispatchData,
  vehicleTypes: DispatcherDispatchProps['vehicleTypes'],
): string => {
  const vehicleTypeNamesMap: Record<string | number, string> = {};
  (Array.isArray(vehicleTypes) ? vehicleTypes : []).forEach((type) => {
    vehicleTypeNamesMap[type.id] = type.name;
    vehicleTypeNamesMap[String(type.id)] = type.name;
    const numericId = Number(type.id);
    if (!Number.isNaN(numericId)) {
      vehicleTypeNamesMap[numericId] = type.name;
    }
  });

  const prefFormatted = (dispatch.vehicle_preferences ?? [])
    .filter((pref) => pref && !pref._destroy && Number(pref.quantity) > 0)
    .map((pref) => {
      const typeName =
        vehicleTypeNamesMap[pref.vehicle_type_id] ?? vehicleTypeNamesMap[String(pref.vehicle_type_id)] ?? '';
      return `${pref.quantity}${typeName}`;
    })
    .join(', ');

  let recipeFormatted = '';
  if (dispatch.vehicle_recipe) {
    const recipe = dispatch.vehicle_recipe;
    recipeFormatted = `${recipe.max_quantity} trucks max; ${recipe.minimum_cuft} CUFT min`;
  }

  return [prefFormatted, recipeFormatted].filter(Boolean).join('; ');
};

const vehicleDetailsFormatted = (
  dispatch: DispatchData,
  vehicleTypes: DispatcherDispatchProps['vehicleTypes'],
): string | undefined => {
  const vehicles = dispatch.vehicles ?? [];
  if (vehicles.length > 0) {
    return vehicles
      .map((v) => v.serial_code)
      .filter(Boolean)
      .join(', ');
  }
  const preferences = (dispatch.vehicle_preferences ?? []).filter((pref) => pref && !pref._destroy);
  if (preferences.length > 0 || dispatch.vehicle_recipe) {
    return vehicleRequestFormatted(dispatch, vehicleTypes);
  }
  return undefined;
};

const scheduleErrors = (dispatch: DispatchData): string[] => {
  if (!dispatch.arrival) {
    return [];
  }
  const arrival = moment(dispatch.arrival);
  return (dispatch.orders ?? [])
    .filter((order) => {
      const scheduledWithOffset = moment(order.scheduled).add(order.dispatch_offset || 0, 'seconds');
      const diff = scheduledWithOffset.diff(arrival, 'minutes');
      return diff < DISPATCHER_CONSTANTS.ARRIVAL_VALID_OFFSET && diff > -200;
    })
    .map(
      (order) => `#${order.number} (${order.account?.customers?.[0]?.name ?? 'Unknown'}) invalid scheduled for arrival`,
    );
};

const formatArrivalTime = (arrival: Date | string, tz: string): string => {
  const dt = DateTime.fromJSDate(new Date(arrival), { zone: tz });
  return `${dt.toFormat('h:mm a')} ${dt.offsetNameShort ?? ''}`.trim();
};

export const DispatcherDispatch: React.FC<DispatcherDispatchProps> = ({
  dispatch,
  region,
  date,
  depots,
  warehouses,
  fieldShiftAssignments,
  vehicleTypes,
  users,
  filters,
  mode,
  routeIndex,
  color,
  dispatchHasWarehouseOrder: hasWarehouseOrderProp,
  moverCountErrors,
  vehicleRequestIconStatus,
  vehicleRequestErrors,
  onRemove,
  onDragstart,
  onDragmove,
  onDragend,
  routeLockActive = true,
}) => {
  const manageMode = mode === 'modifying';
  const routeEditable = (target: DispatchData) => isRouteEditable(target, routeLockActive, manageMode);
  const [calendarRevision, setCalendarRevision] = useState(0);
  const [editing, setEditingState] = useState(!!dispatch._editing);
  const [, setFormRevision] = useState(0);
  const [arrival, setArrival] = useState<Date | null>(() => utcForValueWithTZ(dispatch.arrival ?? null, region.tz));
  const [breakAt, setBreakAt] = useState<Date | null>(() => utcForValueWithTZ(dispatch.break_at ?? null, region.tz));
  const metadataRef = useRef<HTMLDivElement>(null);

  const syncCalendarRevision = useCallback(() => {
    setCalendarRevision((revision) => revision + 1);
  }, []);

  const refreshForm = useCallback(() => {
    setFormRevision((revision) => revision + 1);
  }, []);

  const setEditing = useCallback(
    (value: boolean) => {
      dispatch._editing = value;
      setEditingState(value);
    },
    [dispatch],
  );

  useEffect(() => {
    const onLayoutRefresh = () => {
      setEditingState(!!dispatch._editing);
      syncCalendarRevision();
    };
    document.addEventListener(DISPATCHER_LAYOUT_REFRESH_EVENT, onLayoutRefresh);
    return () => document.removeEventListener(DISPATCHER_LAYOUT_REFRESH_EVENT, onLayoutRefresh);
  }, [dispatch, syncCalendarRevision]);

  const hasWarehouseOrder = hasWarehouseOrderProp || dispatchHasWarehouseOrder(dispatch);
  const errors = useMemo(() => scheduleErrors(dispatch), [dispatch]);
  // Recompute each render — dispatch.vehicles / preferences mutate in place after dialogs.
  const vehicleDetails = vehicleDetailsFormatted(dispatch, vehicleTypes);
  const dispatchDuration = dispatch.formatted_simulated_duration;

  const regionDepots = useMemo(
    () => depots.filter((depot) => find(depot.region_ids, (id) => region.id === id)),
    [depots, region.id],
  );
  const regionWarehouses = useMemo(
    () => warehouses.filter((warehouse) => find(warehouse.region_ids, (id) => region.id === id)),
    [warehouses, region.id],
  );

  useEffect(() => {
    setArrival(utcForValueWithTZ(dispatch.arrival ?? null, region.tz));
  }, [dispatch.arrival, region.tz]);

  useEffect(() => {
    setBreakAt(utcForValueWithTZ(dispatch.break_at ?? null, region.tz));
  }, [dispatch.break_at, region.tz]);

  useEffect(() => {
    const dispatchElement = metadataRef.current?.closest('.dispatcher-dispatch');
    if (!dispatchElement) {
      return undefined;
    }
    dispatchElement.classList.toggle('dispatcher-error', errors.length > 0);
    return () => dispatchElement.classList.remove('dispatcher-error');
  }, [errors]);

  useEffect(() => {
    if (!editing) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (metadataRef.current?.contains(target)) {
        return;
      }
      if (document.querySelector('.modal')?.contains(target)) {
        return;
      }
      if (document.querySelector('.ngdialog')?.contains(target)) {
        return;
      }
      setEditing(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editing, setEditing]);

  const onDispatchUpdated = useCallback(
    (updatedDispatch: DispatchData) => {
      Object.assign(dispatch, updatedDispatch);
    },
    [dispatch],
  );

  const reschedule = useCallback(
    (time: Date) => {
      const nextArrival = localForDateTimeWithTZ(new Date(date), time, region.tz);
      if (nextArrival) {
        dispatch.arrival = nextArrival;
        setArrival(utcForValueWithTZ(nextArrival, region.tz));
      }
    },
    [date, dispatch, region.tz],
  );

  const scheduleBreak = useCallback(
    (time: Date) => {
      const nextBreakAt = localForDateTimeWithTZ(new Date(date), time, region.tz);
      if (nextBreakAt) {
        dispatch.break_at = nextBreakAt;
        setBreakAt(utcForValueWithTZ(nextBreakAt, region.tz));
      }
    },
    [date, dispatch, region.tz],
  );

  const arrivalStartLineStyle = useMemo(() => {
    if (!dispatch.arrival) {
      return undefined;
    }
    return { top: toPercentageStyle(positionForTime(dispatch.arrival, region.tz)) };
  }, [dispatch.arrival, region.tz]);

  const breakAtStartLineStyle = useMemo(() => {
    if (!dispatch.break_at) {
      return undefined;
    }
    const breakEnd = new Date(
      moment(dispatch.break_at)
        .add(DISPATCHER_CONSTANTS.BREAK_LENGTH, DISPATCHER_CONSTANTS.BREAK_LENGTH_UNIT)
        .valueOf(),
    );
    const top = positionForTime(dispatch.break_at, region.tz);
    const bottom = positionForTime(breakEnd, region.tz);
    return {
      top: toPercentageStyle(top),
      height: toPercentageStyle(bottom - top),
      bottom: toPercentageStyle(bottom),
    };
  }, [dispatch.break_at, region.tz]);

  const orderStyle = useCallback((order: DispatchOrder, index: number, grouping: DispatchGrouping) => {
    const position = positionForOrder(
      {
        ...order,
        sla_window_size: order.sla_window_size ?? 1,
        dispatch_offset: order.dispatch_offset ?? 0,
      },
      { offset: order.dispatch_offset ?? 0 },
    );
    const style: React.CSSProperties = {
      top: toPercentageStyle(position.top),
      height: toPercentageStyle(position.bottom - position.top),
      bottom: toPercentageStyle(position.bottom),
    };
    if (index && grouping) {
      style.left = toPercentageStyle(index / grouping.orders.length);
    }
    return style;
  }, []);

  const handleCalendarDragOver = useCallback(
    (event: React.DragEvent) => {
      if (!isRouteEditable(dispatch, routeLockActive, manageMode)) {
        return;
      }
      event.preventDefault();
    },
    [dispatch, manageMode, routeLockActive],
  );

  const handleDragmove = useCallback(
    (order: DispatchOrder, event: DragEvent) => {
      onDragmove(order, event);
    },
    [onDragmove],
  );

  const handleDragend = useCallback(
    (order: DispatchOrder) => {
      onDragend(order);
    },
    [onDragend],
  );

  const timeInputValue = (value: Date | null) => (value ? moment(value).utc().format('HH:mm') : '');

  const handleArrivalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      return;
    }
    const [hours, minutes] = event.target.value.split(':').map(Number);
    const time = new Date();
    time.setUTCHours(hours, minutes, 0, 0);
    reschedule(time);
  };

  const handleBreakChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      return;
    }
    const [hours, minutes] = event.target.value.split(':').map(Number);
    const time = new Date();
    time.setUTCHours(hours, minutes, 0, 0);
    scheduleBreak(time);
  };

  const orders = dispatch.orders ?? [];
  const addons = dispatch.addons ?? [];
  const vehicles = dispatch.vehicles ?? [];
  const vehiclePreferences = dispatch.vehicle_preferences ?? [];

  const hasVehicleConfig = vehiclePreferences.length > 0 || vehicles.length > 0 || !!dispatch.vehicle_recipe;
  const hasInstructions =
    !!dispatch.mover_notes || !!dispatch.warehouse_notes || addons.length > 0 || !!dispatch.load_out;

  return (
    <>
      <div className="dispatcher-metadata" ref={metadataRef}>
        {dispatch.published_at && (
          <div className="published-at">
            Published:{' '}
            <time dateTime={new Date(dispatch.published_at).toISOString()}>
              {DateTime.fromJSDate(new Date(dispatch.published_at)).toFormat('MMM d, yyyy h:mm:ss a')}
            </time>
          </div>
        )}
        <div className="dispatcher-heading">
          <div>
            <div className="dispatcher-title" style={{ color }}>
              Route #{routeIndex + 1}{' '}
              {dispatch.id && (
                <a href={dispatchURL({ id: dispatch.id })} target="_blank" rel="noreferrer" style={{ color }}>
                  ({dispatch.id})
                </a>
              )}
            </div>
          </div>
          {mode === 'modifying' && orders.length === 0 && routeEditable(dispatch) && (
            <a
              className="dispatcher-delete"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                onRemove(dispatch);
              }}
            >
              <span>
                <FontAwesomeIcon icon={faRemove} /> Remove
              </span>
            </a>
          )}
          <div className="dispatcher-error-icons">
            {dispatch.id != null ? (
              <MoverErrorIcon dispatchID={String(dispatch.id)} errors={moverCountErrors} />
            ) : (
              <ErrorIcon
                icon={faUsers}
                status={moverCountErrors.reduce((worst, { status }) => Math.max(status, worst), Status.Good)}
                errors={moverCountErrors.map(({ message }) => message)}
                placement={TooltipPlacement.Bottom}
              />
            )}
            <ErrorIcon
              icon={faTruckMoving}
              status={vehicleRequestIconStatus}
              errors={vehicleRequestErrors}
              placement={TooltipPlacement.Bottom}
            />
            {dispatch.id != null ? (
              <SimulationErrorIcon dispatchID={String(dispatch.id)} />
            ) : (
              <ErrorIcon icon={faHistory} flip="horizontal" status={Status.Off} placement={TooltipPlacement.Bottom} />
            )}
          </div>
        </div>
        <AutoDispatchEnabledCheckbox
          dispatch={dispatch}
          regionID={filters.region_id}
          date={filters.date}
          modifying={mode === 'modifying'}
        />
        <DispatcherAssignmentsManager
          dispatch={dispatch as React.ComponentProps<typeof DispatcherAssignmentsManager>['dispatch']}
          users={users as React.ComponentProps<typeof DispatcherAssignmentsManager>['users']}
          fieldShiftAssignments={
            fieldShiftAssignments as React.ComponentProps<typeof DispatcherAssignmentsManager>['fieldShiftAssignments']
          }
          filters={filters}
          editing={editing && routeEditable(dispatch)}
          onDispatchUpdated={
            onDispatchUpdated as React.ComponentProps<typeof DispatcherAssignmentsManager>['onDispatchUpdated']
          }
        />
        {mode === 'modifying' && editing && routeEditable(dispatch) && (
          <form className="form-horizontal dispatcher-edit-form">
            {hasWarehouseOrder && (
              <div className="form-group">
                <label htmlFor={`dispatch-warehouse-${dispatch.id}`} className="col-xs-3 control-label text-bold">
                  Warehouse
                </label>
                <div className="col-xs-9">
                  <select
                    id={`dispatch-warehouse-${dispatch.id}`}
                    name="dispatch[warehouse]"
                    className="form-control"
                    disabled={!!dispatch.warehouse?.id}
                    value={dispatch.warehouse?.id ?? ''}
                    onChange={(event) => {
                      const warehouse = regionWarehouses.find((w) => String(w.id) === event.target.value);
                      dispatch.warehouse = warehouse ?? null;
                      refreshForm();
                    }}
                  >
                    {!dispatch.warehouse && <option value=""> - Warehouse - </option>}
                    {regionWarehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {!hasWarehouseOrder && (
              <div className="form-group">
                <label htmlFor={`dispatch-depot-${dispatch.id}`} className="col-xs-3 control-label text-bold">
                  Depot
                </label>
                <div className="col-xs-9">
                  <select
                    id={`dispatch-depot-${dispatch.id}`}
                    name="dispatch[depot]"
                    className="form-control"
                    value={dispatch.depot?.id ?? ''}
                    onChange={(event) => {
                      const depot = regionDepots.find((d) => String(d.id) === event.target.value);
                      dispatch.depot = depot ?? null;
                      refreshForm();
                    }}
                  >
                    {!dispatch.depot && <option value=""> Select where movers start the day </option>}
                    {regionDepots
                      .filter((depot) => depot.active === true)
                      .map((depot) => (
                        <option key={depot.id} value={depot.id}>
                          {depot.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
            )}
            <div className="form-group">
              <label htmlFor={`dispatch-arrival-${dispatch.id}`} className="col-xs-3 control-label text-bold">
                Arrival
              </label>
              <div className="col-xs-9">
                <input
                  id={`dispatch-arrival-${dispatch.id}`}
                  type="time"
                  className="form-control form-control-fit"
                  value={timeInputValue(arrival)}
                  onChange={handleArrivalChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor={`dispatch-break-${dispatch.id}`} className="col-xs-3 control-label text-bold">
                Break
              </label>
              <div className="col-xs-9">
                <input
                  id={`dispatch-break-${dispatch.id}`}
                  type="time"
                  className="form-control form-control-fit"
                  value={timeInputValue(breakAt)}
                  onChange={handleBreakChange}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="col-xs-12 text-overflow">
                <DispatcherVehicleRequestButton
                  dispatch={dispatch}
                  onSave={() => {
                    refreshForm();
                  }}
                />
                {hasVehicleConfig && vehicleDetails && <span> {vehicleDetails} </span>}
              </div>
            </div>
            <div className="form-group instructions-form-group">
              <div className="col-xs-12 text-overflow">
                <DispatcherInstructionsButton
                  form={dispatch}
                  onSave={(form) => {
                    dispatch.load_out = form.load_out;
                    dispatch.addons = form.addons;
                    dispatch.warehouse_notes = form.warehouse_notes;
                    dispatch.mover_notes = form.mover_notes;
                    refreshForm();
                  }}
                />
                <div>{dispatch.warehouse_notes || dispatch.mover_notes}</div>
                <div>
                  <span className="dispatcher-text text-overflow">
                    {(dispatch.load_out || addons.length > 0) && <span>Materials:</span>}
                    {dispatch.load_out && <span> {dispatch.load_out}</span>}
                    {addons.length > 0 && (
                      <span>
                        {' '}
                        + <span>{addonsFormatted(addons)} </span>
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </form>
        )}
        <div className="dispatcher-select">
          {!editing && <span className="dispatcher-text text-overflow">{dispatch.depot?.name}</span>}
        </div>
        <div className="dispatcher-select">
          {!editing && (
            <div className="dispatcher-text text-overflow">{hasVehicleConfig && <span> {vehicleDetails} </span>}</div>
          )}
        </div>
        <div className="dispatcher-input">
          {!editing && dispatch.arrival && (
            <span className="dispatcher-text">{formatArrivalTime(dispatch.arrival, region.tz)} Arrival</span>
          )}
          {!editing && dispatchDuration != null && <span className="dispatcher-duration">({dispatchDuration})</span>}
        </div>
        {!editing && (dispatch.warehouse_notes || dispatch.mover_notes) && (
          <div className="dispatcher-instructions">
            <span className="dispatcher-text text-overflow">
              Instructions: {dispatch.warehouse_notes || dispatch.mover_notes}
            </span>
          </div>
        )}
        {!editing && (dispatch.load_out || addons.length > 0) && (
          <div className="dispatcher-materials">
            <span className="dispatcher-text text-overflow">
              <span>Materials:</span>
              {dispatch.load_out && <span> {dispatch.load_out}</span>}
              {addons.length > 0 && (
                <span>
                  {' '}
                  + <span>{addonsFormatted(addons)} </span>
                </span>
              )}
            </span>
          </div>
        )}
        {dispatch.recommendedMovers != null && (
          <div>
            <strong>Recommended Movers: {dispatch.recommendedMovers}</strong>
          </div>
        )}
      </div>
      <div className="dispatcher-calendar" key={calendarRevision} onDragOver={handleCalendarDragOver}>
        {dispatch.arrival && <div className="dispatcher-arrival-line" style={arrivalStartLineStyle} />}
        {dispatch.break_at && <div className="dispatcher-break" style={breakAtStartLineStyle} />}
        {dispatch.groupings?.map((grouping, groupingIndex) => (
          <div key={`${groupingIndex}-${calendarRevision}`} className="dispatcher-groupings">
            {grouping.orders.map((order, orderIndex) => (
              <div
                key={`${order.id}-${calendarRevision}`}
                className="dispatcher-entry"
                style={orderStyle(order, orderIndex, grouping)}
                onDragOver={mode === 'modifying' ? handleCalendarDragOver : undefined}
              >
                <DispatcherOrderPreview
                  order={order}
                  dispatch={dispatch}
                  mode={mode === 'modifying' && routeEditable(dispatch) ? 'modifying' : 'viewing'}
                  onDragstart={onDragstart}
                  onDragmove={onDragmove}
                  onDragend={onDragend}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
      {mode === 'modifying' && !editing && routeEditable(dispatch) && (
        <div
          className="edit-link"
          role="button"
          tabIndex={0}
          aria-label="Edit route"
          title="Edit route"
          onClick={(event) => {
            event.stopPropagation();
            setEditing(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              setEditing(true);
            }
          }}
        >
          <span className="sr-only">Edit</span>
          <FontAwesomeIcon icon={faPencil} />
        </div>
      )}
    </>
  );
};
